import React, { useState, useEffect } from 'react';
import {
  Space,
  Button,
  Dropdown,
  Input,
  Modal,
  message,
  Tooltip,
  Typography,
  Divider,
} from 'antd';
import {
  ShareAltOutlined,
  SettingOutlined,
  CopyOutlined,
  LockOutlined,
  GlobalOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { v2AnalysisApi } from '../../../../api';

const { Text } = Typography;

const ShareSettings = ({
  analysisId,
  analysisStatus,
  initialShareStatus = 'private',
  initialShareUrl = null,
  onShareUpdate = null,
}) => {
  const [shareStatus, setShareStatus] = useState(initialShareStatus);
  const [shareUrl, setShareUrl] = useState(initialShareUrl);
  const [sharePassword, setSharePassword] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  // propsが変更された時に状態を更新
  useEffect(() => {
    console.log('🔄 [ShareSettings] Props changed, updating state:', {
      initialShareStatus,
      initialShareUrl,
    });
    // フォールバックは空値/未定義のときのみ。明示的な値は尊重
    setShareStatus(prev =>
      initialShareStatus ? initialShareStatus : prev || 'private',
    );
    setShareUrl(prev =>
      initialShareUrl !== undefined ? initialShareUrl : prev,
    );
  }, [initialShareStatus, initialShareUrl]);

  // デバッグ情報
  console.log('🔍 [ShareSettings] Props:', {
    analysisId,
    analysisStatus,
    initialShareStatus,
    initialShareUrl,
  });

  console.log('🔍 [ShareSettings] Current state:', {
    shareStatus,
    shareUrl,
  });

  // 分析が完了していない場合は表示しない
  if (analysisStatus !== 'completed') {
    return null;
  }

  // analysisIdが無い場合は表示しない
  if (!analysisId) {
    console.warn('⚠️ [ShareSettings] analysisId is missing');
    return null;
  }

  // 共有ステータスのテキストを取得
  const getShareButtonText = status => {
    switch (status) {
      case 'public':
        return '公開中';
      case 'password':
        return 'パスワード保護';
      case 'private':
      default:
        return '非公開';
    }
  };

  // 共有ステータスのツールチップを取得
  const getShareTooltip = status => {
    switch (status) {
      case 'public':
        return 'この分析結果は誰でも閲覧できます';
      case 'password':
        return 'パスワードを知っている人のみ閲覧できます';
      case 'private':
      default:
        return 'この分析結果は非公開です';
    }
  };

  // 共有ステータスのアイコンを取得
  const getShareIcon = status => {
    switch (status) {
      case 'public':
        return <GlobalOutlined style={{ color: '#52c41a' }} />;
      case 'password':
        return <LockOutlined style={{ color: '#faad14' }} />;
      case 'private':
      default:
        return <EyeInvisibleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // ドロップダウンメニューアイテム
  const getShareMenuItems = () => [
    {
      key: 'private',
      label: (
        <Space>
          <EyeInvisibleOutlined />
          非公開
        </Space>
      ),
      onClick: () => handleShareStatusChange('private'),
    },
    {
      key: 'public',
      label: (
        <Space>
          <GlobalOutlined />
          公開
        </Space>
      ),
      onClick: () => handleShareStatusChange('public'),
    },
    {
      key: 'password',
      label: (
        <Space>
          <LockOutlined />
          パスワード保護
        </Space>
      ),
      onClick: () => setModalVisible(true),
    },
  ];

  // 共有ステータス変更処理
  const handleShareStatusChange = async newStatus => {
    console.log('🔄 [ShareSettings] Changing share status:', {
      analysisId,
      newStatus,
      hasPassword: newStatus === 'password' ? !!sharePassword : false,
    });

    try {
      setShareLoading(true);

      const response = await v2AnalysisApi.setShareStatus(analysisId, {
        status: newStatus,
        password: newStatus === 'password' ? sharePassword : undefined,
      });

      console.log('✅ [ShareSettings] Share status response:', response);

      if (response) {
        setShareStatus(newStatus);
        setShareUrl(response.url);

        if (onShareUpdate) {
          onShareUpdate({
            status: newStatus,
            url: response.url,
          });
        }

        message.success(
          newStatus === 'private'
            ? '非公開に設定しました'
            : '公開設定を保存しました',
        );
      }
    } catch (error) {
      console.error(
        '❌ [ShareSettings] Failed to update share settings:',
        error,
      );
      console.error('❌ [ShareSettings] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        analysisId,
        newStatus,
      });

      if (error.response?.status === 404) {
        message.error('分析結果が見つかりません');
      } else if (error.response?.status === 403) {
        message.error('共有設定の権限がありません');
      } else {
        message.error('共有設定の保存に失敗しました');
      }
    } finally {
      setShareLoading(false);
    }
  };

  // パスワード保護設定の保存
  const handlePasswordProtectionSave = async () => {
    if (!sharePassword.trim()) {
      message.error('パスワードを入力してください');
      return;
    }

    console.log('🔒 [ShareSettings] Setting password protection:', {
      analysisId,
      hasPassword: !!sharePassword,
    });

    try {
      setShareLoading(true);

      const response = await v2AnalysisApi.setShareStatus(analysisId, {
        status: 'password',
        password: sharePassword,
      });

      console.log('✅ [ShareSettings] Password protection response:', response);

      if (response) {
        setShareStatus('password');
        setShareUrl(response.url);
        setModalVisible(false);
        setSharePassword(''); // パスワードをクリア

        if (onShareUpdate) {
          onShareUpdate({
            status: 'password',
            url: response.url,
          });
        }

        message.success('パスワード保護を設定しました');
      }
    } catch (error) {
      console.error('Failed to set password protection:', error);
      if (error.response?.status === 403) {
        message.error(
          'お使いのプランではパスワード保護機能をご利用いただけません',
        );
      } else {
        message.error('パスワード保護の設定に失敗しました');
      }
    } finally {
      setShareLoading(false);
    }
  };

  // 共有URLをコピー
  const copyShareUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      message.success('URLをコピーしました');
    }
  };

  return (
    <Space
      direction="vertical"
      align="end"
      size="small"
      style={{ width: '100%' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <Text type="secondary" style={{ marginRight: '8px', fontSize: '12px' }}>
          共有設定：
        </Text>
        <Tooltip title={getShareTooltip(shareStatus)}>
          <Dropdown
            menu={{ items: getShareMenuItems() }}
            trigger={['click']}
            disabled={shareLoading}
          >
            <Button size="small" loading={shareLoading}>
              {getShareIcon(shareStatus)}
              <span style={{ marginLeft: '4px' }}>
                {getShareButtonText(shareStatus)}
              </span>
              <Divider type="vertical" />
              <SettingOutlined />
            </Button>
          </Dropdown>
        </Tooltip>
      </div>

      {shareStatus !== 'private' && shareUrl && (
        <Input.Group compact style={{ display: 'flex', maxWidth: '300px' }}>
          <Input style={{ flex: 1 }} value={shareUrl} readOnly size="small" />
          <Button icon={<CopyOutlined />} onClick={copyShareUrl} size="small">
            コピー
          </Button>
        </Input.Group>
      )}

      {/* パスワード設定モーダル */}
      <Modal
        title="パスワード保護設定"
        open={modalVisible}
        onOk={handlePasswordProtectionSave}
        onCancel={() => {
          setModalVisible(false);
          setSharePassword('');
        }}
        confirmLoading={shareLoading}
        okText="設定"
        cancelText="キャンセル"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            この分析結果にアクセスするためのパスワードを設定してください。
          </Text>
          <form autoComplete="off" style={{ width: '100%' }}>
            {/* ダミー入力フィールドでパスワードマネージャーを回避 */}
            <input
              type="text"
              name="fake-username"
              autoComplete="username"
              style={{ display: 'none' }}
              tabIndex={-1}
            />
            <input
              type="password"
              name="fake-password"
              autoComplete="new-password"
              style={{ display: 'none' }}
              tabIndex={-1}
            />
            <Input.Password
              placeholder="パスワードを入力"
              value={sharePassword}
              onChange={e => setSharePassword(e.target.value)}
              onPressEnter={handlePasswordProtectionSave}
              autoComplete="new-password"
              data-form-type="other"
            />
          </form>
        </Space>
      </Modal>
    </Space>
  );
};

export default ShareSettings;
